import React, { useEffect, useReducer } from "react";
import { useMediaQuery } from "react-responsive";
import dayjs from "dayjs";
import CategoryFilter from "./CategoryFilter";
import TableControllers from "./TableControllers";
import ProgramList from "./ProgramList";
import StickyHours from "./StickyHours";
import Timeline from "./Timeline";
import TimelineMobile from "./TimelineMobile";
import PROGRAMS from "./programs.json";

const ACTIONS = {
  UPDATE_FILTER: "update_filter",
  UPDATE_DATE: "update_date",
  UPDATE_COUNTER: "update_counter",
  UPDATE_PROGRAMS: "update_programs",
  ADD_COUNTER: "add_counter",
  REMOVE_COUNTER: "remove_counter",
  RELOAD: "reload",
};

const ACTIONS_REDUCERS = {
  [ACTIONS.UPDATE_FILTER]: (state, action) => ({
    ...state,
    filter: action.payload,
  }),
  [ACTIONS.UPDATE_DATE]: (state, action) => ({
    ...state,
    date: action.payload,
  }),
  [ACTIONS.UPDATE_COUNTER]: (state, action) => ({
    ...state,
    counter: action.payload,
  }),
  [ACTIONS.UPDATE_PROGRAMS]: (state, action) => ({
    ...state,
    programs: action.payload,
  }),
  [ACTIONS.ADD_COUNTER]: (state, action) => ({
    ...state,
    counter: state.counter + 1,
  }),
  [ACTIONS.REMOVE_COUNTER]: (state, action) => ({
    ...state,
    counter: state.counter - 1,
  }),
  [ACTIONS.RELOAD]: (state, action) => ({
    ...state,
    reload: !state.reload,
  }),
};

const reducer = (state, action) => {
  const actionReducer = ACTIONS_REDUCERS[action.type];
  return actionReducer ? actionReducer(state, action) : state;
};

const GuideBoard = () => {
  const isDesktopLarge = useMediaQuery({
    query: "(min-width: 1280px)",
  });
  const isDesktopNormal = useMediaQuery({
    query: "(min-width: 1120px)",
  });

  // Variables para colocar en la hora y controlar la posición de timeline
  const hoursNow = dayjs().format("H");
  const minutesNow = dayjs().format("m");
  const hoursNowComp = dayjs().format("HH");
  const minutesNowComp = dayjs().format("mm");
  let emPosition = hoursNow * 2;
  let extraMinutes = minutesNow;
  if (minutesNow > 30) {
    emPosition += 1;
    extraMinutes -= 30;
  }
  const propsTimelines = {
    hoursNowComp,
    minutesNowComp,
    emPosition,
    extraMinutes,
    isDesktopLarge,
    isDesktopNormal,
  };

  const [state, dispatch] = useReducer(reducer, {
    filter: null,
    date: "Hoy",
    counter: emPosition !== 0 && isDesktopNormal ? emPosition - 1 : emPosition,
    programs: [],
    reload: false,
  });

  const widthCol = 235;
  const maxCol = 45;

  // Funciones para editar los estados
  const updateFilter = (data) => {
    dispatch({ type: ACTIONS.UPDATE_FILTER, payload: data });
  };
  const updateDate = (data) => {
    dispatch({ type: ACTIONS.UPDATE_DATE, payload: data });
  };
  const scrollToRight = () => {
    state.counter < maxCol && dispatch({ type: ACTIONS.ADD_COUNTER });
  };
  const scrollToLeft = () => {
    state.counter > 0 && dispatch({ type: ACTIONS.REMOVE_COUNTER });
  };

  useEffect(() => {
    const body = document.querySelector(".body-prog");
    const hours = document.querySelector(".col-hours");
    const programs = document.querySelector(".col-programs");
    hours.scrollLeft = state.counter * widthCol;
    programs.scrollLeft = state.counter * widthCol;
    body.scrollLeft = state.counter * widthCol;
  }, [
    state.filter,
    state.date,
    state.counter,
    state.programs,
    isDesktopNormal,
  ]);

  useEffect(() => {
    // FILTER PROGRAMS AND SET STYLES
    const filteredPrograms = PROGRAMS.channels.map((channel) => {
      if (
        (state.filter && channel.category_id === state.filter) ||
        !state.filter
      ) {
        const cantProgs = channel.programs.length;
        const programs = channel.programs.map((program, i) => {
          let openFromAt; // Variable para modificar estilo de bloque abierto del inicio o del final del row

          const from = program.from.split(":");
          let from_hours;
          if (from[0] < 0) {
            from_hours = 0;
            openFromAt = "open-from";
          } else {
            from_hours = +from[0];
          }
          const from_minutes = +from[1];
          const at = program.at.split(":");
          let at_hours = +at[0];
          let at_minutes = +at[1];
          if (at_hours < from_hours) {
            at_hours = 24;
            at_minutes = 0;
            openFromAt = "open-at";
          }

          const em_hours = 470;
          const em_minutes = 470 / 60;

          const em_from = em_hours * from_hours + em_minutes * from_minutes;
          const em_at = em_hours * at_hours + em_minutes * at_minutes;

          const width = em_at - em_from;
          let marginLeft = em_from;

          let styles = {
            transform: `translateX(${marginLeft}px)`,
            width: `${width}px`,
          };
          i === cantProgs - 1 && (styles = { ...styles, paddingRight: 0 });

          return { ...program, styles, openFromAt };
        });
        channel.programs = programs;
        return channel;
      }
    });
    dispatch({ type: ACTIONS.UPDATE_PROGRAMS, payload: filteredPrograms });
  }, [state.filter]);

  useEffect(() => {
    const intervalTimeline = setInterval(() => {
      dispatch({ type: ACTIONS.RELOAD });
    }, 30000);

    return () => clearInterval(intervalTimeline);
  }, []);

  return (
    <article className="container-programacion">
      <div className="eclipse1"></div>
      <div className="eclipse2"></div>

      <section className="controles-mobile">
        <CategoryFilter
          filterValue={state.filter}
          updateFilter={updateFilter}
          date={state.date}
          updateDate={updateDate}
        />
      </section>

      <section className="programacion">
        <CategoryFilter
          filterValue={state.filter}
          updateFilter={updateFilter}
          date={state.date}
          updateDate={updateDate}
        />
        <TableControllers
          date={state.date}
          scrollToLeft={scrollToLeft}
          scrollToRight={scrollToRight}
          updateDate={updateDate}
        />
        {isDesktopNormal && (
          <Timeline
            counter={state.counter}
            widthCol={widthCol}
            propsTimelines={propsTimelines}
          />
        )}

        <div className="body-prog">
          {!isDesktopNormal && (
            <TimelineMobile
              counter={state.counter}
              widthCol={widthCol}
              propsTimelines={propsTimelines}
            />
          )}
          <div className="col-channels">
            {/* Sticky Header Mobile & Tablet */}
            <div className="row-can sticky">
              <div className="col-can"></div>
            </div>
            {state.programs.map((channel, i) => {
              if (channel)
                return (
                  <div
                    className={`row-can ${i === 0 && "pt-0"}`}
                    key={`channel-${i}`}
                  >
                    <div className="col-can block-prog">
                      <p className="txt-channel">{channel.number}</p>
                      <div className="img-channel">
                        <img src={channel.thumbnail_url} alt={channel.name} />
                      </div>
                    </div>
                  </div>
                );
            })}
          </div>
          <div className="col-programs">
            {/* Sticky Header Mobile & Tablet */}
            <div className="row-prog sticky">
              <StickyHours />
            </div>

            {state.programs.map((channel, i) => {
              if (channel)
                return (
                  <div className="row-prog" key={`programs-${i}`}>
                    <ProgramList list={channel.programs} channelKey={i} />
                  </div>
                );
            })}
          </div>
        </div>
        <div className="shadow-right"></div>
      </section>
    </article>
  );
};

export default GuideBoard;
